import {useLayoutEffect, useState} from 'react'
import PropTypes from 'prop-types'

import {CheckIcon, EyeIcon, RedoIcon, RetryIcon, SparkleIcon, UndoIcon} from './icons'
import {MODE_PROGRESS, ORIGINAL_LABEL, REFINE_STATUS} from './hooks/useRefineStory'

// "Reading your story…" gives way to the mode-specific line after this long.
// There is no streaming to mark the switch, so the change is what tells the
// writer the wait is progressing (AC 4.1, 4.2).
export const READING_LABEL_MS = 1500

/**
 * The answer card's footer while a refine is running (Stop) or waiting for
 * a verdict (Undo / Redo / Show changes / Try again) — TK-3204 sections 4
 * and 5. Idle footer content stays with PromptScreen.
 *
 * The result is already applied and saved, so the review bar closes when
 * the writer goes back to their story (typing or clicking into the box) or
 * moves on. The single-pass refines (Polish, Shorten, a custom request) also
 * get a Done button (`showDone`; prototype 0928) so the writer can close the
 * review and ask for another change; with
 * it showing, Undo, Redo and Try again become icons so the bar stays on one
 * line. `recordVersion` is the optional "Record this version" button; Done
 * sits after it at the far end of the bar.
 *
 * On phones the bar wraps into two rows — [Badge · Undo · Redo · Try again
 * ···· Done] then [Show changes ···· Record this version] — with Undo and
 * Redo shown as icons only; `.ls-refine-foot-break` is the forced line break.
 *
 * The save state is not part of either bar (prototype: Stop takes its place
 * while generating, and the review bar ends with Record this version). The
 * one exception is `saveFailure`: an earlier failed save, with its Retry,
 * must not disappear just because a refine is running or up for review, so
 * PromptScreen passes the indicator only in that state.
 */
const RefineFooter = props => {
  const {status, mode, scope, label} = props
  const [reading, setReading] = useState(true)

  // Before paint, so a second run (Try again) never shows the mode line for
  // a frame before "Reading your story…".
  useLayoutEffect(() => {
    if (status !== REFINE_STATUS.GENERATING) return undefined

    setReading(true)

    const timer = setTimeout(() => setReading(false), READING_LABEL_MS)

    return () => clearTimeout(timer)
  }, [status, mode])

  if (status === REFINE_STATUS.GENERATING) {
    const progressLabel = reading ?
      'Reading your story\u2026' :
      `${MODE_PROGRESS[mode]} your ${scope === 'selection' ? 'selection' : 'story'}\u2026`

    return (
      <div className='ls-write-foot ls-refine-foot'>
        <span className='ls-gen-status'>
          <span aria-hidden='true' className='ls-sparkle'><SparkleIcon size={18} /></span>
          <span>{progressLabel}</span>
        </span>
        <button className='ls-stop-btn' ref={props.stopRef} type='button' onClick={props.onStop}>
          <span aria-hidden='true' className='ls-stop-sq' />Stop <kbd>Esc</kbd>
        </button>
        {props.saveFailure && <span className='ls-refine-foot-save'>{props.saveFailure}</span>}
      </div>
    )
  }

  if (status !== REFINE_STATUS.REVIEW) return null

  return (
    <div className={`ls-write-foot ls-refine-foot ls-refine-foot-review${props.showDone ? ' has-done' : ''}`}>
      <span className={`ls-refine-badge${label === ORIGINAL_LABEL ? ' neutral' : ''}`}>
        <CheckIcon />
        <span>{label}</span>
      </span>
      <button aria-label='Undo' className='ls-txt-btn ls-refine-undo' disabled={!props.canUndo} ref={props.undoRef} title='Undo' type='button' onClick={props.onUndo}>
        <UndoIcon /><span>Undo</span>
      </button>
      <button aria-label='Redo' className='ls-txt-btn ls-refine-redo' disabled={!props.canRedo} title='Redo' type='button' onClick={props.onRedo}>
        <RedoIcon /><span>Redo</span>
      </button>
      <button aria-pressed={props.showingChanges} className='ls-txt-btn ls-refine-changes' disabled={!props.canShowChanges} type='button' onClick={props.onToggleChanges}>
        <EyeIcon /><span>Show changes</span>
      </button>
      <button aria-label='Try again' className='ls-txt-btn ls-refine-retry' disabled={!props.canRetry} title='Try again' type='button' onClick={props.onTryAgain}>
        <RetryIcon />Try again
      </button>
      {props.showDone && <button className='ls-done-btn ls-refine-done' type='button' onClick={props.onDone}>Done</button>}
      <span aria-hidden='true' className='ls-refine-foot-break' />
      {props.recordVersion && <span className='ls-refine-foot-record'>{props.recordVersion}</span>}
      {props.saveFailure && <span className='ls-refine-foot-save'>{props.saveFailure}</span>}
    </div>
  )
}

RefineFooter.propTypes = {
  canRedo: PropTypes.bool,
  canRetry: PropTypes.bool,
  canShowChanges: PropTypes.bool,
  canUndo: PropTypes.bool,
  label: PropTypes.string,
  mode: PropTypes.string,
  onDone: PropTypes.func,
  onRedo: PropTypes.func.isRequired,
  onStop: PropTypes.func.isRequired,
  onToggleChanges: PropTypes.func.isRequired,
  onTryAgain: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  recordVersion: PropTypes.node,
  saveFailure: PropTypes.node,
  scope: PropTypes.string,
  showDone: PropTypes.bool,
  showingChanges: PropTypes.bool,
  status: PropTypes.string.isRequired,
  stopRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),
  undoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})])
}

export default RefineFooter
